import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import StatCard from '../components/StatCard.jsx';
import ChartCard from '../components/ChartCard.jsx';
import TimeSeriesLineChart from '../components/TimeSeriesLineChart.jsx';
import DecimalValueFormat from '../components/DecimalValueFormat.jsx';

import RossDischarge from './RossDischarge.json';
import { getSiteData, getSiteState, getSiteDataSourcesForTypes, getSiteLastData } from '../helpers/SiteData.js';
import { SITE_TYPE_DAM, STATUS_NO_CHANGE, STATUS_NO_RECENT_DATA, STATUS_NO_DATA } from '../helpers/SiteDataConst.js';

const DAM_CHANGE_HOURS = 12;

function RiverDamBlock({site, allData}) {
  const {t} = useTranslation();
  const data = getSiteData(allData, site.id, SITE_TYPE_DAM);
  const latestData = getSiteLastData(allData, site.id, SITE_TYPE_DAM);
  const state = getSiteState(allData, site.id, SITE_TYPE_DAM);
  const dataSource = getSiteDataSourcesForTypes(site, [SITE_TYPE_DAM]);
  const damLevelCapacityStat = determineDamLevelCapacityStat(t, site, latestData);
  const { gateState, dischargeRate } = determineGateOperation(data);

  return (
    <>
      <h2 id={site.id}>{site.name} Conditions</h2>
      <Row>
        <Col md={12} lg={4} className={'mb-4'}>
          <StatCard
            stat={latestData?.value}
            secondStat={damLevelCapacityStat}
            unit={site.unit}
            time={latestData?.timestamp}
            title="Dam Level"
            dataSource={dataSource}
          />
        </Col>
        <Col md={12} lg={4} className={'mb-4'}>
          <StatCard
            stat={gateState}
            secondStat={dischargeRate ? `Discharging ${dischargeRate} m³/s` : undefined}
            disclaimer="Approximation based on TCC Ross River Dam EAP"
            title="Spillway Gates"
          />
        </Col>
        <Col md={12} lg={4} className={'mb-4'}>
          <StatCard
            title={`${DAM_CHANGE_HOURS} Hour Change`}
            stat={[STATUS_NO_CHANGE, STATUS_NO_RECENT_DATA, STATUS_NO_DATA].includes(state?.siteState) ? t(`status.${state?.siteState}.label`) : state?.trend ? t(`trends.${state?.trend}`) : undefined}
            secondStat={state?.trendChange ? `${DecimalValueFormat({value: state.trendChange})} ${t(`units.${site.properties.unit}.label`)} change` : undefined}
            time={data?.[0]?.timestamp}
            twoLineStat={[STATUS_NO_CHANGE, STATUS_NO_RECENT_DATA, STATUS_NO_DATA].includes(state?.siteState)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className={'mb-4'}>
          <ChartCard
            title={`Dam Level (${t(`units.${site.properties.unit}.label`)})`}
            time={data?.[0]?.timestamp}
            dataSource={dataSource}
          >
            <TimeSeriesLineChart
              data={data != undefined ? [data] : undefined}
              dataSeries={[SITE_TYPE_DAM]}
              minDate={DateTime.now().minus({days: 7}).toJSDate()}
              maxDate={DateTime.now().toJSDate()}
              title="Ross River Dam"
              unit={t(`units.${site.properties.unit}.label`)}
              margin={{ top: 20, right: 40, bottom: 60, left: 40 }}
              markers={SiteChartMarkers(t, site, t(`units.${site.properties.unit}.label`))}
            />
          </ChartCard>
        </Col>
      </Row>
    </>
  )
}

function determineDamLevelCapacityStat(t, site, lastData) {
  const damLevelAboveBelow = lastData?.value - site.properties.capacity;
  let damLevelCapacityStat = '';
  if (damLevelAboveBelow > 0) {
    damLevelCapacityStat = t(`levels.above_benchmark`, {
      count: damLevelAboveBelow,
      value: Math.abs(damLevelAboveBelow).toFixed(2),
      units: t(`units.${site.properties.unit}.label`),
      unit: t(`units.${site.properties.unit}.label_singular`),
      benchmark: 'capacity' });
  } else if (damLevelAboveBelow < 0) {
    damLevelCapacityStat = t(`levels.below_benchmark`, {
      count: damLevelAboveBelow,
      value: Math.abs(damLevelAboveBelow).toFixed(2),
      units: t(`units.${site.properties.unit}.label`),
      unit: t(`units.${site.properties.unit}.label_singular`),
      benchmark: 'capacity' });
  } else {
    damLevelCapacityStat = t(`levels.equal_benchmark`, {
      count: damLevelAboveBelow,
      value: Math.abs(damLevelAboveBelow).toFixed(2),
      units: t(`units.${site.properties.unit}.label`),
      unit: t(`units.${site.properties.unit}.label_singular`),
      benchmark: 'capacity' });
  }

  return damLevelCapacityStat
}

function determineGateOperation(lastData, state) {
  if (lastData == undefined || lastData == {}) {
    return { gateState: undefined, dischargeRate: undefined };
  }

  let gateState = 'Closed';
  let dischargeRate = 0;

  const dischargeEntry = RossDischarge.find(
    (entry, index, table) =>
      entry.HeadwaterLevel <= lastData?.value &&
      (index >= table.length - 1 || table[index + 1].HeadwaterLevel > lastData.value)
  );
  if (dischargeEntry) {
    if (dischargeEntry.GatesOpen) {
      gateState = 'Open';
    } else if (dischargeEntry.GatesOpenFully) {
      gateState = 'Fully Open';
    }
    if (state?.trend === 'Rising') {
      dischargeRate = dischargeEntry.DischargeRising;
    } else {
      dischargeRate = dischargeEntry.DischargeFalling;
    }
  }

  return { gateState, dischargeRate };
}

function SiteChartMarkers(t, site= {}, unit) {
  let markers = [];

  if (site.properties?.benchmarks) {
    const chartBenchmarks = site.properties.benchmarks.filter((benchmark) => benchmark.displayOnChart);
    const benchmarkMarkers = chartBenchmarks.map((benchmark) => {
      return {
        axis: 'y',
        value: benchmark.value,
        lineStyle: {
          stroke: '#42708C',
          strokeWidth: 1,
        },
        textStyle: {
          fill: '#42708C',
          fontSize: 10,
        },
        legend: `${benchmark.name} - ${benchmark.value} ${unit}`,
        legendPosition: 'top-right',
        legendOffsetY: 8,
        legendOffsetX: 2,
      };
    });
    markers = markers.concat(benchmarkMarkers)
  }

  return markers
}

export default RiverDamBlock
